import { FormikProps } from 'formik'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { useNavigate } from '@lib/react-router-dom'

import { useAccountInfo } from '@context/AccountInfoContext/AccountInfoContext.Provider'
import { usePathHistoryContext } from '@context/PathHistoryContext'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { useWindowResize } from '@utils/domUtils'

import { LeverageTypeDto, isLeverageTypeDynamic } from '@model/LeverageTypeDto'
import { NameDto } from '@model/NameDto'
import { PlatformTypeEnum } from '@model/PlatformTypeEnum'
import { WalletCurrency } from '@model/WalletDto'

import { CancelActionModal } from '@global/modal/CancleActionModal'
import { Modal } from '@global/modal/Modal'

import { AccountTypeModal } from '../AccountTypeModal'
import { DynamicLeverageModal } from '../DynamicLeverageModal'
import { LeverageModal } from '../LeverageModal'
import { TradingPlatformModal } from '../TradingPlatformModal'
import { ClassificationTypeModal } from './ClassificationTypeModal'
import {
  CreateDemoTradingAccountFormProps,
  CreateDemoTradingAccountFormValues,
  OuterProps,
} from './CreateDemoTradingAccount.types'
import { CurrencyTypeModal } from './CurrencyTypeModal'

const DEFAULT_VIRTUAL_DEPOSIT = 100000

type ModalsProps = CreateDemoTradingAccountFormProps

export const useModals = (props: FormikProps<CreateDemoTradingAccountFormValues> & OuterProps) => {
  const apiClient = useApiClient(ClientApiClient)
  const { values, setFieldValue } = props

  const location = useLocation()

  const [platformTypeModalOpen, setPlatformTypeModalOpen] = useState(false)

  const [leverageModal, setLeverageModal] = useState(false)
  const [dynamicLeverageModal, setDynamicLeverageModal] = useState(false)

  const [accountTypeModal, setAccountTypeModal] = useState(false)
  const [cancelModal, setCancelModal] = useState(false)
  const [accountTypes, setAccountTypes] = useState<NameDto[]>([])
  const [classificationTypes, setClassificationTypes] = useState<NameDto[]>([])

  const [classificationTypeModal, setClassificationTypeModal] = useState(false)
  const [currencyTypeModal, setCurrencyTypeModal] = useState(false)

  const { selectedPlatformType } = location.state || {}

  const [tmpPlatformType, setTmpPlatformType] = useState<NameDto | undefined>(
    values.platformType || selectedPlatformType
  )

  const handleSetTradingPlatform = async (platform?: NameDto) => {
    setPlatformTypeModalOpen(false)
    const currentPlatformType = tmpPlatformType || platform
    setFieldValue('platformType', currentPlatformType)

    if (currentPlatformType) {
      props.onChangePlatform(currentPlatformType?.id)
    }

    if (
      currentPlatformType &&
      (currentPlatformType.id === PlatformTypeEnum.TickmillTrader ||
        currentPlatformType.id === PlatformTypeEnum.TradingView)
    ) {
      const dynamicLeverage = (await apiClient.getLeverageTypes(platform?.id)).find((leverage) =>
        isLeverageTypeDynamic(leverage.id)
      )
      setFieldValue('virtualDeposit', DEFAULT_VIRTUAL_DEPOSIT)
      if (dynamicLeverage) {
        setFieldValue('leverageType', dynamicLeverage)
      }
    } else {
      setFieldValue('leverageType', undefined)
      setFieldValue('virtualDeposit', 0)
    }

    setFieldValue('accountType', undefined)
  }

  const handleSetLeverage = (leverage: LeverageTypeDto) => {
    setFieldValue('leverageType', leverage)
    setLeverageModal(false)
  }

  const handleSetAccountType = (leverage: LeverageTypeDto) => {
    setFieldValue('accountType', leverage)
    setAccountTypeModal(false)
  }

  const handleSetClassificationType = (classificationType: NameDto) => {
    setFieldValue('classificationType', classificationType)
    setClassificationTypeModal(false)
  }

  const handleSetAccountCurrency = (currency: WalletCurrency) => {
    setFieldValue('accountCurrency', currency)
    setCurrencyTypeModal(false)
  }

  const handleSetTmpPlatformType = (platform: NameDto) => {
    setTmpPlatformType(platform)
  }

  return {
    classificationTypes,
    classificationTypeModal,
    accountTypeModal,
    cancelModal,
    platformTypeModalOpen,
    dynamicLeverageModal,
    leverageModal,
    currencyTypeModal,
    accountTypes,
    setAccountTypes,
    setClassificationTypes,
    setCancelModal,
    setLeverageModal,
    setDynamicLeverageModal,
    tmpPlatformType,
    setPlatformTypeModalOpen,
    setAccountTypeModal,
    setCurrencyTypeModal,
    setClassificationTypeModal,
    handleSetTradingPlatform,
    handleSetLeverage,
    handleSetAccountType,
    handleSetClassificationType,
    handleSetAccountCurrency,
    handleSetTmpPlatformType,
  }
}

export const CreateDemoTradingAccountModals = (
  props: ModalsProps & ReturnType<typeof useModals>
) => {
  const { values } = props

  const {
    handleSetTradingPlatform,
    handleSetLeverage,
    handleSetAccountType,
    handleSetClassificationType,
    handleSetAccountCurrency,
    handleSetTmpPlatformType,
    classificationTypes,
    currencies,
    classificationTypeModal,
    accountTypeModal,
    cancelModal,
    platformTypeModalOpen,
    dynamicLeverageModal,
    leverageModal,
    currencyTypeModal,
    accountTypes,
    setCancelModal,
    setLeverageModal,
    setDynamicLeverageModal,
    tmpPlatformType,
    setPlatformTypeModalOpen,
    setAccountTypeModal,
    setCurrencyTypeModal,
    setClassificationTypeModal,
  } = props

  const { t } = useTranslation()
  const { accountInfo } = useAccountInfo()
  const { navigateToPreviousPath } = usePathHistoryContext()

  const navigate = useNavigate()

  const isMobile = useWindowResize()

  return (
    <>
      {currencyTypeModal && (
        <CurrencyTypeModal
          currencies={currencies}
          onSelectOption={handleSetAccountCurrency}
          onClose={() => setCurrencyTypeModal(false)}
        />
      )}
      {classificationTypeModal && (
        <ClassificationTypeModal
          onSelectOption={handleSetClassificationType}
          onClose={() => setClassificationTypeModal(false)}
          classificationTypes={classificationTypes}
        />
      )}
      {!isMobile && platformTypeModalOpen && (
        <TradingPlatformModal
          demo
          tradingAccountsAccountInfo={accountInfo}
          onSelectOption={handleSetTmpPlatformType}
          onConfirm={handleSetTradingPlatform}
          onClose={() => setPlatformTypeModalOpen(false)}
          tmpPlatformType={tmpPlatformType}
        />
      )}
      {dynamicLeverageModal && (
        <DynamicLeverageModal onClose={() => setDynamicLeverageModal(false)} />
      )}
      {leverageModal && (
        <LeverageModal
          title={t('Trading Account.Select Leverage')}
          platformTypeId={values.platformType?.id}
          currentLeverage={values?.leverageType}
          restrictions={[]}
          onSelect={handleSetLeverage}
          onClose={() => setLeverageModal(false)}
        />
      )}
      {accountTypeModal && (
        <AccountTypeModal
          isDemo
          accountTypes={accountTypes}
          accountType={values.accountType}
          onSelect={handleSetAccountType}
          onClose={() => setAccountTypeModal(false)}
        />
      )}
      {cancelModal && (
        <Modal
          closeModal={() => setCancelModal(false)}
          render={() => (
            <CancelActionModal
              onConfirm={() => {
                if (isMobile) {
                  navigate('/dashboard/traders-room/trading-accounts', { replace: true })
                } else {
                  navigateToPreviousPath({ state: { fromAddDemoTradingAccountForm: true } })
                }
              }}
              onCancel={() => setCancelModal(false)}
            />
          )}
        />
      )}
    </>
  )
}
